/** Design tokens for admin-app — neutral/professional palette. */
export const tokens = {
  color: {
    // ── Brand / Primary ──────────────────────────────────────────────────
    brand: "#F97316",        // bright orange — buttons, highlights (Orders, OrderDetail, Reports)
    brandAmber: "#D97706",   // amber — Ant Design colorPrimary, Categories, Hours, Menu
    brandMid: "#F5841F",     // mid orange — Dashboard stat cards
    brandWarm: "#C2410C",    // dark orange text — ActivityLog, Refunds
    brandLight: "#FFF7ED",   // lightest orange bg — Settings sections, Stalls
    brandFaint: "#FFFBF5",   // faintest orange — IssueRefund selected card
    brandAmberBg: "#FEF3C7", // amber bg — Refunds PROCESSING, ActivityLog
    brandAmberText: "#92400E", // amber text — Refunds, ActivityLog, Reports
    brandChartA: "#FBBF24",  // chart orange-yellow A — Reports pie
    brandChartB: "#FCD34D",  // chart orange-yellow B — Reports pie

    // ── Sidebar / Shell ──────────────────────────────────────────────────
    siderBg: "#111827",      // sidebar background (Ant Design override)
    siderSection: "#1F2937", // sidebar sub-section / trigger
    siderHover: "#374151",   // sidebar item hover
    siderText: "#D1D5DB",    // sidebar item text
    siderActive: "#D97706",  // sidebar active item bg
    headerBg: "#FFFFFF",     // top header background

    // ── Text hierarchy ───────────────────────────────────────────────────
    textBase: "#111827",     // theme.ts colorTextBase — darkest (Ant Design base)
    textDark: "#212121",     // headings (Dashboard, Orders, Login)
    textBody: "#616161",     // body text (Dashboard, Staff, ActivityLog)
    textMuted: "#9E9E9E",    // muted/caption (Dashboard, Orders, ReceiptModal)
    textSecondary: "#6B7280",// Ant Design colorTextSecondary
    textLight: "#D1D5DB",    // lightest text (login, sidebar)
    textDeep: "#1C1C1C",     // deepest near-black (Login bg gradient start)

    // ── Backgrounds ──────────────────────────────────────────────────────
    bg: "#F9FAFB",           // Ant Design colorBgLayout — app layout bg
    bgCard: "#F8F8F8",       // card/stat bg (Dashboard)
    surface: "#FFFFFF",      // white card surface
    surfaceSection: "#F3F4F6", // section divider / neutral bg (many screens)
    surfaceGreen: "#F0FDF4", // very light green bg — Settings/Stalls status
    surfaceBlue: "#EFF6FF",  // very light blue bg — Settings display-order section
    surfaceOrange: "#FFF7ED",// very light orange bg — Settings/Stalls section headers
    surfaceYellow: "#FFF8E1",// light yellow bg — pending/new order status
    surfaceTeal: "#CCFBF1",  // light teal bg — out_for_delivery status
    surfacePurple: "#EDE7F6",// light purple bg — confirmed status

    // ── Borders ──────────────────────────────────────────────────────────
    border: "#E5E7EB",       // Ant Design colorBorder — primary border
    borderMid: "#EBEBEB",    // card separator (Dashboard chart)
    borderLight: "#D1D5DB",  // lighter border / emoji bg (Bundles)

    // ── Status: Success / Green ──────────────────────────────────────────
    successText: "#166534",  // dark green text — ACTIVE, ready badge, ActivityLog create
    successDeep: "#14532D",  // deepest green — delivered/completed bg-as-text
    successGreen: "#10B981", // Ant Design colorSuccess
    successMid: "#4CAF50",   // mid green — Dashboard stat
    successBright: "#16A34A",// bright green — Bundles savings, Staff avatar
    successBg: "#DCFCE7",    // light green bg — create pill, savings badge
    successBgAlt: "#D1FAE5", // alt light green — Refunds COMPLETED

    // ── Status: Danger / Red ─────────────────────────────────────────────
    dangerText: "#DC2626",   // red text — Ant Design colorError-like, OrderDetail
    dangerDeep: "#991B1B",   // dark red — Refunds REJECTED text
    dangerMid: "#EF4444",    // Ant Design colorError
    dangerBg: "#FEE2E2",     // light red bg — delete pill, Transactions REFUNDED

    // ── Status: Warning / Amber ──────────────────────────────────────────
    warningText: "#F59E0B",  // Ant Design colorWarning
    warningDeep: "#854D0E",  // dark brown — Transactions PENDING text
    warningBg: "#FEF3C7",    // light yellow bg — note pill, PROCESSING

    // ── Status: Info / Blue ──────────────────────────────────────────────
    infoText: "#3B82F6",     // Ant Design colorInfo / Staff avatar
    infoDark: "#1E40AF",     // dark blue — ActivityLog update text
    infoDeep: "#1D4ED8",     // deeper blue — Reports
    infoBg: "#DBEAFE",       // light blue bg — ActivityLog update, Reports
    infoBgAlt: "#EFF6FF",    // lighter blue bg — Settings display-order

    // ── Status: Teal ─────────────────────────────────────────────────────
    tealText: "#00BCD4",     // teal accent — Dashboard, Orders/OrderDetail C.teal
    tealDark: "#0E7490",     // dark teal — ReceiptModal primary
    tealMid: "#0D9488",      // mid teal — ReceiptModal accent
    tealDeep: "#0F766E",     // deep teal — out_for_delivery text
    tealBg: "#CCFBF1",       // teal bg — out_for_delivery badge bg

    // ── Status: Purple ───────────────────────────────────────────────────
    purpleText: "#7E22CE",   // dark purple — ActivityLog login text
    purpleMid: "#9333EA",    // mid purple — Staff avatar
    purpleDeep: "#6A1B9A",   // deep purple — confirmed order status
    purpleBg: "#F3E8FF",     // light purple bg — ActivityLog login pill, confirmed
    purpleBgAlt: "#EDE7F6",  // alt purple bg — confirmed status

    // ── Order status badges ──────────────────────────────────────────────
    statusPreparingBg: "#FFF3E0",  // bg — preparing
    statusPreparingText: "#F97316",// text — preparing
    statusReadyBg: "#166534",      // bg — ready (dark green)
    statusReadyText: "#FFFFFF",    // text — ready
    statusDeliveryBg: "#CCFBF1",   // bg — out_for_delivery
    statusDeliveryText: "#0F766E", // text — out_for_delivery
    statusTransitBg: "#F3F4F6",    // bg — in_transit
    statusTransitText: "#6B7280",  // text — in_transit
    statusDeliveredBg: "#14532D",  // bg — delivered/completed (dark green)
    statusDeliveredText: "#FFFFFF",// text — delivered/completed
    statusCancelledBg: "#F3F4F6",  // bg — cancelled
    statusCancelledText: "#9E9E9E",// text — cancelled
    statusPendingBg: "#FFF8E1",    // bg — pending/new
    statusPendingText: "#F57F17",  // text — pending/new
    statusConfirmedBg: "#EDE7F6",  // bg — confirmed
    statusConfirmedText: "#6A1B9A",// text — confirmed

    // ── Activity log pill colors ─────────────────────────────────────────
    activityCreateBg: "#DCFCE7",  activityCreateText: "#166534",
    activityUpdateBg: "#EFF6FF",  activityUpdateText: "#1E40AF",
    activityDeleteBg: "#FEE2E2",  activityDeleteText: "#DC2626",
    activityLoginBg: "#F3E8FF",   activityLoginText: "#7E22CE",
    activityOtherBg: "#F3F4F6",   activityOtherText: "#616161",
    activityWarnBg: "#FFF7ED",    activityWarnText: "#C2410C",
    activityNoteBg: "#FEF3C7",    activityNoteText: "#92400E",

    // ── Staff avatar palette ─────────────────────────────────────────────
    avatarOrange: "#F97316",
    avatarGreen: "#16A34A",
    avatarBlue: "#3B82F6",
    avatarPurple: "#9333EA",
    avatarPink: "#EC4899",
    avatarAmber: "#F59E0B",
    avatarCyan: "#0891B2",

    // ── Receipt / special ────────────────────────────────────────────────
    receiptBg: "#1A1A1A",    // barcode / very dark bg in receipt
    loginGradientStart: "#1C1C1C",
    loginGradientEnd: "#2D1B00",
    loginLogoBg: "#F97316",

    // ── Picked-up / misc ─────────────────────────────────────────────────
    pickedUpBg: "#3B2A1A",   // dark brown — Orders "Picked Up" button
    chartBarBg: "#EEEEEE",   // bar chart track background
    disabledText: "#6B7280", // DISABLED badge text (Bundles)
    black: "#000000",
    darkGray: "#191C1D",
    brown: "#855300",
  },

  radius: {
    xs: 4,
    sm: 8,    // Ant Design borderRadius override
    md: 12,   // Ant Design Card borderRadiusLG override
    lg: 16,
    xl: 24,
    pill: 9999,
  },

  shadow: {
    card: "0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px -1px rgba(0,0,0,0.1)",
    cardElevated: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)",
    modal: "0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
    dropdown: "0 4px 12px rgba(0,0,0,0.15)",
  },

  space: { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32 },

  font: { family: '"Noto Sans", "DM Sans", system-ui, sans-serif' },

  layout: {
    siderWidth: 220,
  },
} as const;

// ── Convenience re-export ─────────────────────────────────────────────────────
export const C = tokens.color;

// ── Legacy theme object kept for internal consumers that still import it.
export const kawkawTheme = {
  token: {
    colorPrimary:        C.brandAmber,
    colorSuccess:        C.successGreen,
    colorWarning:        C.warningText,
    colorError:          C.dangerMid,
    colorInfo:           C.infoText,
    borderRadius:        tokens.radius.sm,
    fontFamily:          tokens.font.family,
    fontSize:            14,
    colorBgLayout:       C.bg,
    colorBgContainer:    C.surface,
    colorBorder:         C.border,
    colorTextBase:       C.textBase,
    colorTextSecondary:  C.textSecondary,
  },
  components: {
    Layout: {
      siderBg:   C.siderBg,
      triggerBg: C.siderSection,
    },
    Menu: {
      darkItemBg:          C.siderBg,
      darkSubMenuItemBg:   C.siderSection,
      darkItemSelectedBg:  C.siderActive,
      darkItemHoverBg:     C.siderHover,
      darkItemColor:       C.siderText,
      darkItemSelectedColor: C.surface,
    },
    Table: {
      headerBg:   C.surfaceSection,
      rowHoverBg: C.warningBg,
    },
    Card: {
      borderRadiusLG: tokens.radius.md,
    },
    Button: {
      borderRadius: tokens.radius.sm,
    },
  },
};
